/**
 * @module typescript-fsa
 *
 * TypeScript FSA
 * https://github.com/aikoven/typescript-fsa
 *
 * @license
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2016 Daniel Lytkin
 * Copyright (c) 2025 Morris Allison III
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
export function isAnyAction(value) {
    return typeof value === "object" && value !== null && "type" in value;
}
export function isAction(value) {
    return (isAnyAction(value) && typeof value.type === "string" && "payload" in value);
}
/**
 * Returns `true` if action has the same type as action creator.
 * Defines Type Guard that lets TypeScript know `payload` type inside blocks
 * where `isType` returned `true`.
 *
 * @example
 *
 *    const somethingHappened =
 *      actionCreator<{foo: string}>('SOMETHING_HAPPENED');
 *
 *    if (isType(action, somethingHappened)) {
 *      // action.payload has type {foo: string}
 *    }
 */
export function isType(action, actionCreator) {
    return action.type === actionCreator.type;
}
/**
 * Creates Action Creator factory with optional prefix for action types.
 * @param prefix Prefix to be prepended to action types as `<prefix>/<type>`.
 * @param defaultIsError Function that detects whether action is error given the
 *   payload. Default is `payload => payload instanceof Error`.
 */
export function actionCreatorFactory(prefix, defaultIsError = (p) => p instanceof Error) {
    const actionTypes = {};
    const base = prefix ? `${prefix}/` : "";
    function actionCreator(type, commonMeta, isError = defaultIsError) {
        const fullType = base + type;
        if (actionTypes[fullType])
            throw new Error(`Duplicate action type: ${fullType}`);
        actionTypes[fullType] = true;
        return Object.assign((payload, meta) => {
            const action = {
                type: fullType,
                payload,
            };
            if (commonMeta || meta) {
                action.meta = Object.assign({}, commonMeta, meta);
            }
            if (isError && (typeof isError === "boolean" || isError(payload))) {
                action.error = true;
            }
            return action;
        }, {
            type: fullType,
            toString: () => fullType,
            match: (action) => action.type === fullType,
        });
    }
    // Knyt Change: Removed async action creators
    return actionCreator;
}
